import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'
import { docPath } from '../lib/types'
import { Button, EmptyState, Spinner, VisibilityBadge } from '../components/ui'

export function FavoritesPage() {
  const { user, loading: authLoading, signIn } = useAuth()

  const { data, loading, error } = useAsync(
    (signal) => (user ? api.favorites(signal) : Promise.resolve([])),
    [user?.uid, authLoading],
  )

  if (authLoading) return <Wrap><Spinner /></Wrap>

  if (!user) {
    return (
      <Wrap>
        <EmptyState title="Sign in to see your saved pages">
          <Button variant="primary" size="sm" className="mt-4" onClick={() => void signIn()}>
            Sign in
          </Button>
        </EmptyState>
      </Wrap>
    )
  }

  return (
    <Wrap>
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-ink">Saved pages</h1>

      {loading && <Spinner />}
      {error && <p className="text-sm text-danger">Could not load favorites.</p>}

      {data && data.length === 0 && (
        <EmptyState title="Nothing saved yet">Star a doc to keep it here.</EmptyState>
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-line border-y border-line">
          {data.map((d) => (
            <li key={d.id}>
              <Link to={docPath(d)} className="block py-4 transition-colors hover:bg-sunken">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{d.title}</span>
                  <VisibilityBadge visibility={d.visibility} />
                </div>
                <p className="mt-1 font-mono text-xs text-subtle">
                  {d.service} / {d.topic}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Wrap>
  )
}

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-3xl px-1 py-10 lg:px-8">{children}</div>
)
